import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface Heartbeat {
  device_id: string;
  created_at: string;
  // Live metrics — only present on real device_heartbeats rows, not on
  // historical 'alive' events recovered from device_logs.
  signal?: number | null;
  free_heap?: number | null;
  uptime_seconds?: number | null;
}

const PAGE_SIZE = 1000;
const MAX_PAGES = 50;
const DAYS = 365;
const CONCURRENCY = 8;
// Safety cap for the historical alive-event archive (~100k rows today).
const MAX_ALIVE_ROWS = 150_000;

async function fetchHeartbeats(sinceIso: string): Promise<Heartbeat[]> {
  const all: Heartbeat[] = [];
  for (let page = 0; page < MAX_PAGES; page++) {
    const { data, error } = await (supabase as any)
      .from('device_heartbeats')
      .select('device_id, created_at, signal, free_heap, uptime_seconds')
      .gte('created_at', sinceIso)
      .order('created_at', { ascending: false })
      .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);
    if (error) throw error;
    if (!data?.length) break;
    all.push(...data);
    if (data.length < PAGE_SIZE) break;
  }
  return all;
}

/**
 * Historical pulse: before the device_heartbeats table existed, firmware
 * logged periodic 'alive' events to device_logs. Fetch that archive (only
 * the part older than the first real heartbeat, to avoid double counting)
 * with parallel pagination — the archive holds 100k+ rows.
 */
async function fetchAliveHistory(sinceIso: string, beforeIso: string): Promise<Heartbeat[]> {
  const base = () =>
    (supabase as any)
      .from('device_logs')
      .select('id, device_mac, created_at')
      .eq('event', 'alive')
      .gte('created_at', sinceIso)
      .lt('created_at', beforeIso);

  // Find the id bounds of the matching rows (fast primary-key lookups).
  const [{ data: lo, error: loErr }, { data: hi, error: hiErr }] = await Promise.all([
    base().order('id', { ascending: true }).limit(1),
    base().order('id', { ascending: false }).limit(1),
  ]);
  if (loErr) throw loErr;
  if (hiErr) throw hiErr;
  if (!lo?.length || !hi?.length) return [];
  const minId = lo[0].id as number;
  const maxId = hi[0].id as number;

  // Offset pagination times out on this 750k-row log table, so paginate by
  // id cursor (keyset) across parallel id-range lanes.
  const LANES = CONCURRENCY;
  const span = maxId - minId + 1;
  const laneBounds = Array.from({ length: LANES }, (_, i) => ({
    from: minId + Math.floor((span * i) / LANES),
    to: i === LANES - 1 ? maxId + 1 : minId + Math.floor((span * (i + 1)) / LANES),
  }));

  const fetchLane = async (from: number, to: number): Promise<Heartbeat[]> => {
    const out: Heartbeat[] = [];
    const laneCap = Math.ceil(MAX_ALIVE_ROWS / LANES) + PAGE_SIZE;
    let cursor = from - 1;
    for (;;) {
      const { data, error } = await base()
        .gt('id', cursor)
        .lt('id', to)
        .order('id', { ascending: true })
        .limit(PAGE_SIZE);
      if (error) throw error;
      if (!data?.length) break;
      data.forEach((r: { device_mac: string; created_at: string }) =>
        out.push({ device_id: r.device_mac, created_at: r.created_at }),
      );
      cursor = data[data.length - 1].id as number;
      if (data.length < PAGE_SIZE || out.length >= laneCap) break;
    }
    return out;
  };

  const lanes = await Promise.all(laneBounds.map((l) => fetchLane(l.from, l.to)));
  return lanes.flat();
}

/**
 * Fetches the full heartbeat archive once so every heartbeat chart on the
 * page shares a single fetch: recent rows from device_heartbeats, extended
 * backwards with historical 'alive' events from device_logs. `missing` means
 * neither source could be read.
 */
export function useHeartbeats(days = DAYS) {
  const [beats, setBeats] = useState<Heartbeat[] | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    const since = new Date(Date.now() - days * 864e5).toISOString();

    (async () => {
      // Where does the real heartbeat archive start? Everything before that
      // moment is recovered from device_logs 'alive' events.
      let epoch: string | null = null;
      try {
        const { data: first } = await (supabase as any)
          .from('device_heartbeats')
          .select('created_at')
          .order('created_at', { ascending: true })
          .limit(1);
        epoch = first?.[0]?.created_at ?? null;
      } catch {
        // device_heartbeats table not readable — history only.
      }

      const [recent, history] = await Promise.all([
        fetchHeartbeats(since).catch(() => null as Heartbeat[] | null),
        fetchAliveHistory(since, epoch ?? new Date().toISOString()).catch(
          () => null as Heartbeat[] | null,
        ),
      ]);

      if (!recent && !history) {
        setMissing(true);
        return;
      }
      const merged = [...(recent ?? []), ...(history ?? [])];
      merged.sort((a, b) => a.created_at.localeCompare(b.created_at));
      setBeats(merged);
    })();
  }, [days]);

  return { beats, missing, loading: beats === null && !missing };
}
